import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Row,
  Col,
  Select,
  Button,
  Typography,
  Space,
  Dropdown,
  Menu,
  Tooltip,
  Popover,
  Checkbox,
  Slider,
  Radio,
} from "antd";
import sortIcon from "../../assests/Images/sortIcon.svg";
import ProductCardNew from "./ProductCardNew";
import {
  InfoCircleOutlined,
  StarFilled,
  GlobalOutlined,
  FilterOutlined,
  CloseOutlined,
  LeftCircleFilled,
  RightCircleFilled,
} from "@ant-design/icons";
import ProductSkeleton from "./ProductSkeleton";
import { formatNumber } from "../../shared/utils";
import { ProductCardV2 } from "./ProductCardV2";
import { ElectronicsTab } from "./ElectronicsTab";
import { ElectronicsTabV2 } from "./ElectronicsTabV2";
import ProductCardSkeleton from "./ProductCardSkeleton";

const { Title } = Typography;

const ProductListSection = ({
  bestValueProducts = [],
  topRatedProducts = [],
  lowestPricedProducts = [],
  loadingSkeleton,
  phoneNumber,
  pageView,
  amazonSavingamount,
  amazonSavingPercentage,
  shopperooRender,
  partialData,
}) => {
  const [sortOption, setSortOption] = useState("bestValue");
  const [isSortOptionChanged, setIsSortOptionChanged] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);

  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(true);

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({
        left: 0,
        behavior: "smooth",
      });

      // reset arrow visibility
      setShowLeftButton(false);
      setShowRightButton(container.scrollWidth > container.clientWidth);
    }
  }, [sortOption, selectedStores]);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = direction === "left" ? -200 : 200;
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setShowLeftButton(container.scrollLeft > 0);
      setShowRightButton(
        container.scrollLeft <
          container.scrollWidth - container.clientWidth - 10
      );
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Get all unique stores from all products
  const allStores = useMemo(() => {
    const allProducts = [
      ...bestValueProducts,
      ...topRatedProducts,
      ...lowestPricedProducts,
    ];
    const stores = [
      ...new Set(allProducts.map((p) => p.store).filter(Boolean)),
    ];
    return stores.sort();
  }, [bestValueProducts, topRatedProducts, lowestPricedProducts]);

  // Get the current products array based on sort option
  const getCurrentProducts = () => {
    switch (sortOption) {
      case "bestValue":
        return bestValueProducts;
      case "topRated":
        return topRatedProducts;
      case "priceLow":
        return lowestPricedProducts;
      default:
        return bestValueProducts;
    }
  };

  // Filter products based on selected stores and price range
  const getFilteredProducts = () => {
    let products = getCurrentProducts();

    // Filter by store
    if (selectedStores.length > 0) {
      products = products.filter((p) => selectedStores.includes(p.store));
    }

    // Filter by price range
    products = products.filter((p) => {
      const price = p.price || p.unit_price || 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });

    return products;
  };

  useEffect(() => {
    if (sortOption === "bestValue" && bestValueProducts?.length === 0) {
      if (topRatedProducts?.length > 0) {
        setSortOption("topRated");
      } else if (lowestPricedProducts?.length > 0) {
        setSortOption("priceLow");
      }
    }
  }, [bestValueProducts, topRatedProducts, lowestPricedProducts]);

  const handleSortChange = (value) => {
    setSortOption(value);
  };

  const handleStoreChange = (store) => {
    setSelectedStores((prev) =>
      prev.includes(store) ? prev.filter((s) => s !== store) : [...prev, store]
    );
  };

  const handlePriceRangeChange = (value) => {
    setPriceRange(value);
  };

  const clearFilters = () => {
    setSelectedStores([]);
    setPriceRange([0, 10000]);
    setSortOption("bestValue");
    setIsSortOptionChanged(false);
  };

  const hasActiveFilters =
    selectedStores.length > 0 ||
    priceRange[0] !== 0 ||
    priceRange[1] !== 10000 ||
    isSortOptionChanged;

  const currentProducts = getFilteredProducts();

  // Get first product from each array for tab display
  const getTabInfo = (products) => {
    if (!products || products?.length === 0) return null;
    const first = products[0];
    return {
      price: first?.price || first?.unit_price,
      rating: first?.rating,
      reviews: first?.reviews,
      store: first?.store,
    };
  };

  const bestValueInfo = getTabInfo(bestValueProducts);
  const topRatedInfo = getTabInfo(topRatedProducts);
  const lowestPriceInfo = getTabInfo(lowestPricedProducts);

  const tabs = [
    {
      key: "bestValue",
      label: "Best Value",
      info: bestValueInfo,
      tooltip: "Smartly sorted for the best bang for your buck!",
    },
    {
      key: "topRated",
      label: "Top Rated",
      info: topRatedInfo,
      tooltip: "Crowd favorites!",
    },
    {
      key: "priceLow",
      label: "Budget",
      info: lowestPriceInfo,
      tooltip: "Most affordable options - no filters!",
    },
  ];

  const shouldHighlightFirst = sortOption;

  // Filter Popover Content
  const filterContent = (
    <div style={{ width: "280px", padding: "10px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <span style={{ fontWeight: "600", fontSize: "16px" }}>Filters</span>
        {hasActiveFilters && (
          <Button
            type="text"
            size="small"
            onClick={clearFilters}
            style={{ color: "#ff4d4f", padding: "0 8px" }}
          >
            Clear All
          </Button>
        )}
      </div>
      <div style={{ marginBottom: "20px" }}>
        <div style={{ fontWeight: "600", marginBottom: "10px" }}>Sort By</div>
        <Radio.Group
          className="flex flex-col gap-2"
          onChange={(e) => {
            setIsSortOptionChanged(true);
            handleSortChange(e.target.value);
          }}
          value={sortOption}
          options={[
            {
              value: "bestValue",
              label: (
                <div className="flex items-center gap-2">
                  <span>Best Value</span>

                  <Tooltip
                    title="Smartly sorted for the best bang for your buck!"
                    trigger="hover"
                  >
                    <InfoCircleOutlined
                      className="text-gray-400 hover:text-blue-500 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                </div>
              ),
            },
            {
              value: "topRated",
              label: (
                <div className="flex items-center gap-2">
                  <span>Top Rated</span>

                  <Tooltip title="Crowd favorites!" trigger="hover">
                    <InfoCircleOutlined
                      className="text-gray-400 hover:text-blue-500 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                </div>
              ),
            },
            {
              value: "priceLow",
              label: (
                <div className="flex items-center gap-2">
                  <span>Budget</span>

                  <Tooltip
                    title="Most affordable options - no filters!"
                    trigger="hover"
                  >
                    <InfoCircleOutlined
                      className="text-gray-400 hover:text-blue-500 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                </div>
              ),
            },
          ]}
        />
      </div>

      {/* Store Filter */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ fontWeight: "600", marginBottom: "10px" }}>Store</div>
        <div style={{ maxHeight: "200px", overflowY: "auto" }}>
          {allStores.map((store) => (
            <div key={store} style={{ marginBottom: "8px" }}>
              <Checkbox
                checked={selectedStores.includes(store)}
                onChange={() => handleStoreChange(store)}
              >
                {store}
              </Checkbox>
            </div>
          ))}
          {allStores.length === 0 && (
            <div style={{ color: "#999", fontSize: "14px" }}>
              No stores available
            </div>
          )}
        </div>
      </div>

      {/* Price Range Filter */}
      <div>
        <div style={{ fontWeight: "600", marginBottom: "10px" }}>
          Price Range: ${priceRange[0]} - ${priceRange[1]}
        </div>
        <Slider
          range
          min={0}
          max={10000}
          step={100}
          value={priceRange}
          onChange={handlePriceRangeChange}
          tooltip={{ formatter: (value) => `$${value}` }}
        />
      </div>

      <div
        style={{
          marginTop: "15px",
          paddingTop: "15px",
          borderTop: "1px solid #f0f0f0",
          fontSize: "12px",
          color: "#666",
        }}
      >
        Showing {currentProducts.length}{" "}
        {currentProducts.length === 1 ? "product" : "products"}
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => {
            setFilterPopoverVisible(false);
          }}
          className="bg-black text-white px-3 py-[3px] rounded-lg text-[13px] font-medium"
        >
          Apply
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* {!shopperooRender &&
        amazonSavingPercentage >= 20 &&
        currentProducts &&
        currentProducts?.length > 0 && (
          <Typography
            style={{
              fontWeight: "500",
              textAlign: "left",
              padding: "10px 0px",
              fontSize: "14px",
            }}
          >
            🏆 Our best value pick is <b>${amazonSavingamount} less</b> than
            Amazon’s first pick!
          </Typography>
        )}

      {!shopperooRender && currentProducts && currentProducts?.length > 0 && (
        <Typography
          style={{
            fontWeight: "500",
            textAlign: "left",
            padding: "1px 0px 10px 0px",
            fontSize: "14px",
          }}
        >
          Click the{" "}
          <span
            style={{
              // background: "#00d4d4",
              background: "black",
              color: "white",
              padding: "0px 6px",
              borderRadius: "6px",
            }}
          >
            ‘Visit Store’ button
          </span>{" "}
          to buy directly on Amazon, Walmart & more.
        </Typography>
      )} */}

      {pageView && (
        <Typography
          style={{
            fontWeight: "600",
            textAlign: "center",
            padding: "10px",
            fontSize: "16px",
          }}
        >
          <b>To unlock the full list, simply Text Roo</b>
        </Typography>
      )}

      <div
        style={{
          padding: pageView ? "10px 10px" : "10px 0px",
          background: pageView ? "black" : "transparent",
        }}
      >
        {/* Tab Navigation */}
        {/* {currentProducts && currentProducts?.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: "4px",
              marginBottom: "20px",
              overflowX: "auto",
              paddingBottom: "10px",
            }}
          > */}
        {/* {tabs.map((tab) => {
              const isActive = sortOption === tab.key;
              const info = tab.info;

              if (!info) {
                return null;
              }

              return (
                <div
                  key={tab.key}
                  onClick={() => handleSortChange(tab.key)}
                  style={{
                    flex: "1",
                    minWidth: "120px",
                    padding: "10px 0px",
                    background: isActive ? "#000" : "#f5f5f5",
                    color: isActive ? "#fff" : "#333",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    opacity: pageView && !isActive ? "0.4" : "1",
                    borderBottom: isActive
                      ? "3px solid #000"
                      : "3px solid transparent",
                    textAlign: "center",
                    transform: isActive ? "translateY(0px)" : "translateY(0)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                      marginBottom: "6px",
                      fontSize: "13px",
                      fontWeight: "800",
                    }}
                  >
                    {tab.label}
                    {tab.tooltip && (
                      <Tooltip title={tab.tooltip} placement="bottom">
                        <InfoCircleOutlined style={{ fontSize: "12px" }} />
                      </Tooltip>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>${info?.price?.toFixed(2)}</span>
                  </div>

                  {info && info.rating ? (
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "2px",
                        }}
                      >
                        {info.rating}
                        <StarFilled
                          style={{
                            fontSize: "11px",
                            color: isActive ? "#ffd700" : "#333",
                          }}
                        />
                      </span>
                      {info.rating ? (
                        <span>({formatNumber(info.reviews)})</span>
                      ) : (
                        ""
                      )}
                    </div>
                  ) : (
                    ""
                  )}
                  {isActive && (
                    <div
                      style={{
                        background: "#090909",
                        position: "absolute",
                        width: "100%",
                        marginTop: "18px",
                        borderRadius: "2px",
                        height: "4px",
                      }}
                    ></div>
                  )}
                </div>
              );
            })} */}

        {/* Sort By with Filter Button */}
        {/* <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "60px",
              gap: "4px",
              position: "relative"
            }}>
              <Popover
                content={filterContent}
                title={null}
                trigger="click"
                placement="bottomRight"
                open={filterPopoverVisible}
                onOpenChange={setFilterPopoverVisible}
              >
                <div style={{ cursor: "pointer", position: "relative" }}>
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "3px"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                      <div style={{ width: "14px", height: "2px", background: pageView ? "#666" : "#333" }}></div>
                      <div style={{ width: "4px", height: "4px", background: pageView ? "#666" : "#333", borderRadius: "50%" }}></div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                      <div style={{ width: "10px", height: "2px", background: pageView ? "#666" : "#333" }}></div>
                      <div style={{ width: "4px", height: "4px", background: pageView ? "#666" : "#333", borderRadius: "50%" }}></div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                      <div style={{ width: "16px", height: "2px", background: pageView ? "#666" : "#333" }}></div>
                      <div style={{ width: "4px", height: "4px", background: pageView ? "#666" : "#333", borderRadius: "50%" }}></div>
                    </div>
                  </div>
                  {hasActiveFilters && (
                    <div style={{
                      position: "absolute",
                      top: "-4px",
                      right: "-4px",
                      width: "8px",
                      height: "8px",
                      background: "#ff4d4f",
                      borderRadius: "50%",
                      border: "2px solid white"
                    }}></div>
                  )}
                </div>
              </Popover>
              <span style={{ fontSize: "11px", fontWeight: "600", color: pageView ? "#666" : "#333", marginTop: "2px" }}>
                Sort By
              </span>
            </div> */}
        {/* </div>
        )} */}

        {currentProducts && currentProducts?.length > 0 && (
          // <ElectronicsTab
          //   activeTab={sortOption}
          //   hasActiveFilters={hasActiveFilters}
          //   onChange={(selectedTab) => handleSortChange(selectedTab)}
          //   filterContent={filterContent}
          //   open={filterPopoverVisible}
          //   onOpenChange={setFilterPopoverVisible}
          //   tabsData={[
          //     {
          //       key: "bestValue",
          //       label: "Best Value",
          //       info: bestValueInfo,
          //       tooltip: "Smartly sorted for the best bang for your buck!",
          //     },
          //     {
          //       key: "topRated",
          //       label: "Highest Rated",
          //       info: topRatedInfo,
          //       tooltip: "Crowd favorites!",
          //     },
          //     {
          //       key: "priceLow",
          //       label: "Cheapest",
          //       info: lowestPriceInfo,
          //       tooltip: "Most affordable options - no filters!",
          //     },
          //   ]}
          // />
          <ElectronicsTabV2
            hasActiveFilters={hasActiveFilters}
            onChange={(selectedTab) => handleSortChange(selectedTab)}
            filterContent={filterContent}
            open={filterPopoverVisible}
            onOpenChange={setFilterPopoverVisible}
            amazonSavingamount={amazonSavingamount}
            amazonSavingPercentage={amazonSavingPercentage}
          />
        )}

        {/* Active Filters Display */}
        {/* {hasActiveFilters && ( */}
        {/* <div
          style={{
            marginBottom: "15px",
            display: "flex",
            gap: "8px",
            alignItems: "flex-start",
          }}
        > */}
        {/* Filter By Button */}
        {/* {currentProducts && currentProducts?.length > 0 && (
            <Popover
              content={filterContent}
              title={null}
              trigger="click"
              placement="bottomRight"
              open={filterPopoverVisible}
              onOpenChange={setFilterPopoverVisible}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  minWidth: "60px",
                  gap: "4px",
                  flexShrink: 0,
                }}
              >
                <div style={{ cursor: "pointer", position: "relative" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "3px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                      }}
                    >
                      <div
                        style={{
                          width: "14px",
                          height: "2px",
                          background: pageView ? "#666" : "#333",
                        }}
                      ></div>
                      <div
                        style={{
                          width: "4px",
                          height: "4px",
                          background: pageView ? "#666" : "#333",
                          borderRadius: "50%",
                        }}
                      ></div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                      }}
                    >
                      <div
                        style={{
                          width: "10px",
                          height: "2px",
                          background: pageView ? "#666" : "#333",
                        }}
                      ></div>
                      <div
                        style={{
                          width: "4px",
                          height: "4px",
                          background: pageView ? "#666" : "#333",
                          borderRadius: "50%",
                        }}
                      ></div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                      }}
                    >
                      <div
                        style={{
                          width: "16px",
                          height: "2px",
                          background: pageView ? "#666" : "#333",
                        }}
                      ></div>
                      <div
                        style={{
                          width: "4px",
                          height: "4px",
                          background: pageView ? "#666" : "#333",
                          borderRadius: "50%",
                        }}
                      ></div>
                    </div>
                  </div>
                  {hasActiveFilters && (
                    <div
                      style={{
                        position: "absolute",
                        top: "-4px",
                        right: "-4px",
                        width: "8px",
                        height: "8px",
                        background: "#ff4d4f",
                        borderRadius: "50%",
                        border: "2px solid white",
                      }}
                    ></div>
                  )}
                </div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color: pageView ? "#666" : "#333",
                    marginTop: "2px",
                  }}
                >
                  {hasActiveFilters ? "Filtered By " : "Filter By"}
                </span>
              </div>
            </Popover>
          )} */}

        {/* Filter Chips Container */}
        {/* <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              alignItems: "center",
              flex: 1,
            }}
          >
            {selectedStores.map((store) => (
              <span
                key={store}
                style={{
                  background: "#f0f0f0",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {store}
                <CloseOutlined
                  style={{ fontSize: "10px", cursor: "pointer" }}
                  onClick={() => handleStoreChange(store)}
                />
              </span>
            ))}
            {(priceRange[0] !== 0 || priceRange[1] !== 10000) && (
              <span
                style={{
                  background: "#f0f0f0",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                ${priceRange[0]} - ${priceRange[1]}
                <CloseOutlined
                  style={{ fontSize: "10px", cursor: "pointer" }}
                  onClick={() => setPriceRange([0, 10000])}
                />
              </span>
            )}
          </div>
        </div> */}
        {/* )} */}

        {/* Product List */}
        <div>
          {loadingSkeleton && (
            <div
              style={{
                display: "flex",
                overflowX: "auto",
                scrollBehavior: "smooth",
                paddingBottom: "8px",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
                marginTop: "20px",
              }}
              className="hide-scrollbar"
            >
              {/* <ProductSkeleton />
              <ProductSkeleton /> */}

              {[0, 1, 2].map((index) => (
                <ProductCardSkeleton
                  key={index}
                  index={index}
                  partialData={partialData}
                />
              ))}
            </div>
          )}

          {!loadingSkeleton && currentProducts.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                color: "#999",
              }}
            >
              <FilterOutlined
                style={{ fontSize: "48px", marginBottom: "16px" }}
              />
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "8px",
                }}
              >
                No products found
              </div>
              <div style={{ fontSize: "14px", marginBottom: "16px" }}>
                Try adjusting your filters
              </div>
              <Button onClick={clearFilters}>Clear Filters</Button>
            </div>
          )}

          <div style={{ position: "relative" }}>
            {showLeftButton && (
              <Button
                shape="circle"
                icon={<LeftCircleFilled style={{ fontSize: "24px" }} />}
                onClick={() => scroll("left")}
                style={{
                  position: "absolute",
                  left: "-12px",
                  transform: "translateY(350%)",
                  zIndex: 10,
                  width: "48px",
                  height: "48px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "white",
                  border: "1px solid #d9d9d9",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              />
            )}

            {showRightButton && (
              <Button
                shape="circle"
                icon={<RightCircleFilled style={{ fontSize: "24px" }} />}
                onClick={() => scroll("right")}
                style={{
                  position: "absolute",
                  right: "-2px",
                  transform: "translateY(340%)",
                  zIndex: 10,
                  width: "48px",
                  height: "48px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "white",
                  border: "1px solid #d9d9d9",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              />
            )}
          </div>
          <div
            ref={scrollContainerRef}
            style={{
              display: "flex",
              overflowX: "auto",
              scrollBehavior: "smooth",
              paddingBottom: "8px",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
            className="hide-scrollbar"
          >
            {currentProducts?.map((product, index) => {
              const highlightBestValue = shouldHighlightFirst && index === 0;

              return (
                <ProductCardV2
                  product={product}
                  sortOption={sortOption}
                  index={index}
                  isHighlighted={index === 0}
                  phoneNumber={phoneNumber}
                />
              );

              // <ProductCardNew
              //       {...product}
              //       phoneNumber={phoneNumber}
              //       sortOption={sortOption}
              //       index={index}
              //       pageView={pageView}
              //     />

              return (
                <div
                  key={`${product.id || product.key || "product"}-${index}`}
                  style={{
                    marginBottom: "12px",
                    position: "relative",
                    border: highlightBestValue
                      ? "2px solid black"
                      : "1px solid #f0f0f0",
                    borderRadius: "10px",
                  }}
                >
                  {/* Best Value Ribbon */}
                  {highlightBestValue && (
                    <div
                      style={{
                        position: "absolute",
                        left:
                          shouldHighlightFirst === "topRated"
                            ? "-56px"
                            : shouldHighlightFirst === "bestValue"
                            ? "-44px"
                            : "-42px",
                        top: "50%",
                        transform: "translateY(-50%) rotate(-90deg)",
                        background: "black",
                        opacity: pageView ? "0.3" : "1",
                        color: "white",
                        fontSize: "12px",
                        fontWeight: "bold",
                        padding: "2px 10px",
                        borderRadius: "4px",
                        whiteSpace: "nowrap",
                        zIndex: 10,
                      }}
                    >
                      {shouldHighlightFirst === "bestValue"
                        ? "Best Value"
                        : shouldHighlightFirst === "topRated"
                        ? "Top Rated"
                        : "Budget"}
                    </div>
                  )}

                  <ProductCardNew
                    {...product}
                    phoneNumber={phoneNumber}
                    sortOption={sortOption}
                    index={index}
                    pageView={pageView}
                  />
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </>
  );
};

export default ProductListSection;
